<template>
  <v-dialog v-model="dialog" max-width="1100" scrollable persistent no-click-animation>
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs">
        <a v-on="on" v-bind="attrs">Изменить ставку</a>
      </slot>
    </template>

    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Ставки по налогам и накладным</span>
        <v-spacer />
        <v-select
          v-model="year"
          :items="yearItems"
          label="Год"
          dense
          outlined
          hide-details
          style="max-width: 160px"
          @change="loadYear"
        />
        <v-btn small text class="ml-2" @click="addYear">+ год</v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text style="max-height: 60vh">
        <v-progress-linear v-if="loading" indeterminate color="primary" />
        <p class="text--secondary mt-2 mb-4" v-if="!loading">
          Значения — проценты. Незаполненная ячейка = ставка не задана для этой организации.
        </p>
        <v-simple-table v-if="!loading" dense>
          <thead>
            <tr>
              <th style="min-width: 240px">Организация</th>
              <th v-for="a in articles" :key="a.key" class="text-center">{{ a.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="row.key">
              <td>{{ row.label }}</td>
              <td v-for="a in articles" :key="a.key" class="px-1">
                <v-text-field
                  v-model="model[row.key][a.key]"
                  type="number"
                  dense
                  hide-details
                  outlined
                  suffix="%"
                  class="rate-cell"
                />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <span v-if="savedAt" class="text--secondary ml-2">Сохранено</span>
        <v-spacer />
        <v-btn text @click="dialog = false">Закрыть</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="loading" @click="save">Сохранить</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/modules/api';
import {
  RATE_ARTICLES,
  FL_KEY,
  FL_LABEL,
  getRateConfig,
  getRateConfigYears,
  saveRateConfig,
} from '@/modules/RateConfig';

export default {
  name: 'RateConfigDialog',
  model: { prop: 'value', event: 'input' },
  props: {
    // year to open on (e.g. the project year)
    defaultYear: { type: [Number, String], default: () => new Date().getFullYear() },
    // optional external open control (v-model); the activator slot also works
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: this.value,
      loading: true,
      saving: false,
      savedAt: false,
      articles: RATE_ARTICLES,
      year: Number(this.defaultYear) || new Date().getFullYear(),
      years: [],
      ourOrgs: [], // [{id, name}]
      model: {}, // { <rowKey>: { <articleKey>: '' | number } }
    };
  },
  computed: {
    rows() {
      return [
        ...this.ourOrgs.map((o) => ({ key: String(o.id), label: o.name })),
        { key: FL_KEY, label: FL_LABEL },
      ];
    },
    // only rows whose model bucket is ready (guards the async gap on first open)
    visibleRows() {
      return this.rows.filter((r) => this.model[r.key]);
    },
    yearItems() {
      const set = new Set([...this.years, this.year]);
      return [...set].sort((a, b) => b - a);
    },
  },
  watch: {
    value(v) {
      this.dialog = v;
    },
    dialog(open) {
      this.$emit('input', open);
      if (open) this.init();
    },
  },
  methods: {
    blankModel() {
      const m = {};
      for (const row of this.rows) {
        m[row.key] = {};
        for (const a of this.articles) m[row.key][a.key] = '';
      }
      return m;
    },
    async init() {
      this.loading = true;
      this.savedAt = false;
      this.model = this.blankModel(); // never leave model empty while the table renders
      try {
        const [orgsRes, years] = await Promise.all([
          api.get('supmain/legalentities?page=0&size=1000&sort=id,asc&search=isOurOrg:true'),
          getRateConfigYears(),
        ]);
        this.ourOrgs = (orgsRes.payload || []).map((o) => ({
          id: o.id,
          name: o.shortName || o.fullName || `#${o.id}`,
        }));
        this.years = years;
        this.year = Number(this.defaultYear) || this.years[0] || new Date().getFullYear();
        await this.loadYear();
      } finally {
        this.loading = false;
      }
    },
    async loadYear() {
      this.loading = true;
      this.savedAt = false;
      try {
        const cfg = await getRateConfig(this.year);
        const m = this.blankModel();
        for (const row of this.rows) {
          const bucket = cfg[row.key];
          if (!bucket) continue;
          for (const a of this.articles) {
            if (typeof bucket[a.key] === 'number') m[row.key][a.key] = bucket[a.key];
          }
        }
        this.model = m;
      } finally {
        this.loading = false;
      }
    },
    addYear() {
      const input = window.prompt('Год для нового конфига:', String(this.year + 1));
      if (input == null) return;
      const y = Number(input);
      if (!y || Number.isNaN(y) || y < 2000 || y > 2100) {
        window.alert('Некорректный год');
        return;
      }
      if (this.yearItems.includes(y)) {
        window.alert(`Год ${y} уже есть — выберите его в списке.`);
        return;
      }
      this.years.push(y);
      this.year = y;
      this.model = this.blankModel();
      this.savedAt = false;
    },
    async save() {
      this.saving = true;
      try {
        // strip empty cells, coerce to numbers
        const out = {};
        for (const row of this.rows) {
          const src = this.model[row.key] || {};
          const bucket = {};
          for (const a of this.articles) {
            const raw = src[a.key];
            if (raw === '' || raw === null || raw === undefined) continue;
            const n = Number(raw);
            if (!Number.isNaN(n)) bucket[a.key] = n;
          }
          if (Object.keys(bucket).length) out[row.key] = bucket;
        }
        const res = await saveRateConfig(this.year, out);
        if (res && res.ok) {
          this.savedAt = true;
          if (!this.years.includes(this.year)) this.years.push(this.year);
          this.$emit('saved', { year: this.year, config: out });
        } else {
          window.alert('Не удалось сохранить (нужна роль ADMIN?)');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.rate-cell {
  max-width: 120px;
}
/* give rows enough height so the outlined inputs don't overlap the row border */
.v-data-table >>> tbody td {
  height: 60px;
  padding-top: 6px;
  padding-bottom: 6px;
  vertical-align: middle;
}
.v-data-table >>> thead th {
  height: 44px;
  white-space: normal;
}
</style>
